import { observer } from 'mobx-react';
import React from 'react';
import { Section } from 'controls/react/layout/section';
import { FormEntryNew } from 'controls/react/form/formEntryNew';
import { AntInput } from 'controls/react/ant/antInput';
import { AntSelect } from 'controls/react/ant/antSelect';
import { PivotSettings } from 'controls/designer/features/widgets/datasources/shared/pivotSettings';
import {
	DatasourceChartConfigModelBase,
	DatasourceConfigModelBase,
} from 'controls/designer/features/widgets/datasources/shared/datasourceConfigBase';
import { AntCheckbox } from 'controls/react/ant/antCheckbox';
import { i18n } from 'controls/designer/features/widgets/datasources/shared/translations';
import { DataSource } from 'areas/data-sources/types/dataSource';
import { DataSourceSelector } from 'controls/designer/features/widgets/datasources/shared/components/data-source-selector/dataSourceSelector';
import { DatasourceEditor } from 'controls/designer/features/widgets/datasources/shared/components/data-source-editor/datasourceEditor';
import { PivotManager } from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/pivotManager';
import { PivotModel } from 'controls/designer/features/widgets/datasources/shared/utils/pivot-manager/types';
import { DataSourceManager } from 'controls/designer/features/widgets/datasources/shared/utils/data-source-manager/dataSourceManager';

export type ChartTypeOptions = {
	value: string;
	label: string;
	type?: string;
	chart3DOptions?: any;
};
export const getChartTypeOptions = () => {
	return [
		{ label: i18n('Bar chart'), value: 'bar' },
		{ label: i18n('Pie chart'), value: 'pie' },
	];
};

export const getPieSubTypeOptions = () => {
	return [
		{ label: i18n('Pie'), value: '2d-pie', type: 'pie' },
		{
			label: i18n('3D Pie'),
			value: '3d-pie',
			type: 'pie',
			chart3DOptions: {
				enabled: true,
				alpha: 45,
				beta: 0,
			},
		},
		{
			label: i18n('Donut'),
			value: 'donut',
			type: 'pie',
			series: { innerSize: '60%' },
		},
	];
};

export const getBarSubTypeOptions = () => {
	return [
		{ label: i18n('Vertical'), value: 'column', type: 'column' },
		{ label: i18n('Horizontal'), value: 'bar', type: 'bar' },
		{ label: i18n('Vertical stacked'), value: 'column-stacked', type: 'column' },
		{ label: i18n('Horizontal stacked'), value: 'bar-stacked', type: 'bar' },
	];
};

export interface CommonSettingsDatasourceConfigStore<TModel extends DatasourceConfigModelBase<TModel>> {
	model: TModel;
	pieSubTypes: ChartTypeOptions[];
	barSubTypes: ChartTypeOptions[];
	chartTypes: ChartTypeOptions[];
	dataSources?: DataSource[];
	manager: DataSourceManager;
	pivotManager: PivotManager<PivotModel>;
}

export const CommonSettings = observer(
	<TModel extends DatasourceChartConfigModelBase<TModel>>(props: {
		store: CommonSettingsDatasourceConfigStore<TModel>;
		hideChartType: boolean;
		disableDrillDown?: boolean;
	}) => {
		const { store } = props;
		return (
			<>
				<Section appearance={'frame-top-only'} title={i18n('General')} childrenPadding={true}>
					<FormEntryNew label={i18n('Title')} model={store.model} modelField={'title'}>
						<AntInput />
					</FormEntryNew>
					{!props.hideChartType && (
						<FormEntryNew label={i18n('Chart')} model={store.model} modelField={'chartType'}>
							<AntSelect nameField={'name'} options={store.chartTypes} />
						</FormEntryNew>
					)}
					{store.model.chartType == 'pie' && (
						<FormEntryNew label={i18n('Chart type')} model={store.model} modelField={'chartSubType'}>
							<AntSelect nameField={'name'} options={store.pieSubTypes} />
						</FormEntryNew>
					)}
					{store.model.chartType == 'bar' && (
						<FormEntryNew label={i18n('Chart type')} model={store.model} modelField={'chartSubType'}>
							<AntSelect nameField={'name'} options={store.barSubTypes} />
						</FormEntryNew>
					)}
				</Section>
				<Section appearance={'frame-top-only'} title={i18n('Data')} childrenPadding={true}>
					<DatasourceEditor manager={store.manager} showRowEditor={false} />
					{!props.disableDrillDown && (
						<>
							<FormEntryNew model={store.model} modelField={'enableDrillDown'}>
								<AntCheckbox>{i18n('Show datasource grid on click')}</AntCheckbox>
							</FormEntryNew>
							<FormEntryNew
								label={
									store.model.drilldownDataSourceId
										? i18n('Drilldown Datasource')
										: i18n('Use Chart Datasource (set above)')
								}
								model={store.model}
								modelField={'drilldownDataSourceId'}
							>
								<DataSourceSelector manager={store.manager} allowClear={true} />
							</FormEntryNew>
						</>
					)}
					<PivotSettings store={store.pivotManager} />
				</Section>
			</>
		);
	},
);
